/**
 * Ce que le candidat doit savoir AVANT de toucher au poste — UNE seule copie.
 *
 * Kayla, 06-10-2026 : « une sorte d'introduction où il faut qu'ils
 * apprennent ». Le même texte sert deux fois : en gros sur l'écran
 * d'introduction (`IntroDuTest`), et en petit dans l'onglet « Aide » du
 * panneau de droite pendant la simulation. Deux copies divergeraient au premier
 * mot changé — et c'est exactement la leçon des pages de doc du 19-08, qui
 * enseignaient encore `/menu` dix jours après son retrait.
 *
 * ⚠️ Le vocabulaire est celui du POSTE, mot pour mot : « Ma file »,
 * « Discussions », « Ajouter », « Flow ▾ », « À faire maintenant »,
 * « Flow terminé », « Reprendre ». Un mot de la leçon qu'il ne retrouve pas à
 * l'écran est un mot qui l'égare.
 *
 * Phrases courtes, vocabulaire du quotidien : la surface est EMPLOYÉE, et une
 * partie des candidats lit le français en seconde langue (Kayla, 17-09 :
 * « des gens du tiers monde »).
 */
import { PLANCHER_DE_LA_VIDEO_CENTS, VIDEO_A_30, VIDEO_A_50 } from "./scenario";

export interface Lecon {
  cle: string;
  emoji: string;
  titre: string;
  /** Un paragraphe par entrée. Le gras se pose avec **…**. */
  texte: readonly string[];
}

const PRIX = `${VIDEO_A_50.prixCents / 100} €`;
const PLANCHER = `${PLANCHER_DE_LA_VIDEO_CENTS / 100} €`;

export const LECONS: readonly Lecon[] = [
  {
    cle: "travail",
    emoji: "💬",
    titre: "Ton travail",
    texte: [
      "Tu discutes avec les fans d'une créatrice, à sa place. **Cyrano**, notre IA, leur parle toute la journée.",
      "Toi, tu prends la main au bon moment : tu accueilles les **nouveaux** fans, tu leur annonces le prix, et tu encaisses.",
    ],
  },
  {
    cle: "file",
    emoji: "📥",
    titre: "Ta file",
    texte: [
      "**Ma file**, ce sont les conversations dont TU t'occupes (15 au maximum).",
      "Les autres attendent dans l'onglet **Discussions**. Pour en prendre une, tu touches **Ajouter**.",
      "Un fan marqué **NOUVEAU** vient d'arriver : c'est lui qu'on prend en premier.",
    ],
  },
  {
    cle: "flow",
    emoji: "⚙️",
    titre: "Le flow",
    texte: [
      "Un flow, c'est une suite de messages déjà écrits que Cyrano envoie **tout seul**, un par un, pour accueillir un **nouveau** fan.",
      "Dès qu'un nouveau fan arrive, tu lances le flow **« script 1 »** : la barre **À faire maintenant** te le propose, ou le bouton **Flow ▾** sous le champ.",
      "Pendant qu'il tourne, **tu n'écris rien**. Tu attends **Flow terminé** et l'alerte qui te dit **« à toi »**.",
    ],
  },
  {
    cle: "offre",
    emoji: "💶",
    titre: "Ce que tu vends",
    texte: [
      "Quand le flow est fini, le fan demande combien.",
      `Tu lui vends **UN média**, avec son prix : ici **ta vidéo de 15 min, en direct, à ${PRIX}**. Pas une liste, pas un menu.`,
      "Tu peux l'écrire toi-même, ou prendre la phrase toute prête dans l'onglet **Scripts** (« Après le flow »).",
    ],
  },
  {
    // 🎯 Kayla, 06-10-2026 : « c'est la stratégie qu'ils suivent […] c'est
    // important de connaître le contenu de la bouteille pour pouvoir vendre. »
    // Quatre règles, et chacune est un critère du test (`contenu`, `pour_lui`,
    // `valeur`).
    cle: "strategie",
    emoji: "🎯",
    titre: "Vendre : la stratégie",
    texte: [
      "**Connais ce que tu vends, et décris-le.** La durée de la vidéo, ce qu'elle fait dedans. La fiche de chaque média est dans l'onglet **Vault**, à droite.",
      "**Fais-lui sentir que c'est fait pour lui, en direct** : « je te la fais en direct, juste pour toi ». Ce qu'on vend a de la valeur parce qu'il croit que c'est à lui.",
      `**Tiens ton prix.** Pour le même média, tu peux baisser jusqu'à **${PLANCHER} au plus bas** (les frais, c'est fait pour lui). Jamais moins.`,
      `**En dessous, propose un autre média, plus petit** (par exemple une vidéo à ${VIDEO_A_30.prixCents / 100} € où elle se doigte et se caresse) — **jamais le même** moins cher.`,
    ],
  },
  {
    cle: "transaction",
    emoji: "💳",
    titre: "La transaction",
    texte: [
      "Quand il dit oui, tu touches le bouton **$** (Vente) sous le champ : tu choisis un **produit vidéo**, tu mets **le prix que vous avez conclu**, et dans **Délivrer au paiement** tu choisis **LA vidéo que tu lui as vendue**. Puis **Envoyer**.",
      "Le fan reçoit un **lien de paiement**. Quand il paie, tu le vois dans la conversation.",
      "Ne coche pas « Il a déjà payé » : ça enregistre une vente sans rien lui envoyer.",
    ],
  },
  {
    cle: "francis",
    emoji: "🧠",
    titre: "Francis",
    texte: [
      "L'onglet **Francis**, à droite, est là pour t'aider : il résume le fan, te dit quoi lui vendre, et prépare la vente pour toi (**Préparer la vente**).",
      "Tu relis, puis c'est toi qui envoies.",
    ],
  },
  {
    cle: "reprendre",
    emoji: "▶️",
    titre: "Reprendre",
    texte: [
      "Quand c'est payé, tu touches **Reprendre** : tu rends la main à Cyrano, qui continue la conversation avec le fan.",
    ],
  },
];

/**
 * Découpe un paragraphe en morceaux normaux et en morceaux **gras** — le seul
 * balisage des leçons. Exporté pour que les deux rendus (intro, onglet Aide) le
 * partagent.
 */
export function morceaux(texte: string): Array<{ gras: boolean; texte: string }> {
  return texte
    .split(/(\*\*[^*]+\*\*)/g)
    .filter(Boolean)
    .map((m) =>
      m.startsWith("**") && m.endsWith("**")
        ? { gras: true, texte: m.slice(2, -2) }
        : { gras: false, texte: m },
    );
}

/**
 * La vidéo de Kayla — « Les règles fondamentales de la vente ».
 *
 * Servie par le stockage de fichiers Convex (mp4 public, 169 Mo, `Accept-Ranges`
 * : le navigateur ne charge que ce qu'il lit). `convex.dozka.app` est dans
 * `media-src` de la CSP (`deploy/nginx.conf`, les trois blocs) — l'hôte des
 * vidéos de formation depuis le départ de Spaces (05-10-2026), auto-hébergé sur
 * le VDS depuis le 15-10-2026 (le fichier a gardé son id à l'import).
 */
export const VIDEO_DE_KAYLA = {
  titre: "Les règles fondamentales de la vente",
  url: "https://convex.dozka.app/api/storage/3f822a05-dbb1-47ec-88df-985fa16eb1f5",
} as const;
